import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, IndianRupee, MessageCircle, TrendingUp, MapPin, Zap, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";

const getCategoryStyle = (category) => {
  const cat = category?.toUpperCase() || '';
  if (cat.includes('AGRO')) return 'bg-green-100 text-green-700 border-green-200';
  if (cat.includes('SERVICE')) return 'bg-blue-100 text-blue-700 border-blue-200';
  if (cat.includes('CHEMICAL') || cat.includes('POLYMER')) return 'bg-purple-100 text-purple-700 border-purple-200';
  if (cat.includes('MINERAL')) return 'bg-amber-100 text-amber-700 border-amber-200';
  if (cat.includes('FOREST')) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (cat.includes('RURAL') || cat.includes('ENGINEERING')) return 'bg-slate-100 text-slate-700 border-slate-200';
  if (cat.includes('TEXTILE')) return 'bg-pink-100 text-pink-700 border-pink-200';
  return 'bg-gray-100 text-gray-600 border-gray-200';
};

const formatCost = (cost) => {
  if (cost >= 10000000) return `${(cost / 10000000).toFixed(1)} करोड़`;
  if (cost >= 100000) return `${(cost / 100000).toFixed(1)} लाख`;
  if (cost >= 1000) return `${(cost / 1000).toFixed(0)} हज़ार`;
  return cost.toString();
};

const formatCostFull = (cost) => {
  return new Intl.NumberFormat('hi-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(cost);
};

const getRiskColor = (risk) => {
  const r = risk?.toLowerCase() || '';
  if (r.includes('low') || r.includes('कम')) return 'text-green-600';
  if (r.includes('high') || r.includes('उच्च')) return 'text-red-600';
  return 'text-yellow-600';
};

// Generate estimated highlights based on project cost and category
const getEstimatedHighlights = (project) => {
  const cost = project.total_cost;
  const category = project.category?.toUpperCase() || '';
  
  // Estimate monthly profit (10-15% of investment per year)
  let monthly_profit = Math.round(cost * 0.12 / 12);
  
  // Estimate area based on cost
  let area, power, risk;
  if (cost < 500000) {
    area = 200;
    power = "सिंगल फेज";
    risk = "कम";
  } else if (cost < 1500000) {
    area = 500;
    power = "सिंगल फेज";
    risk = "मध्यम";
  } else {
    area = 1000;
    power = "थ्री फेज";
    risk = "मध्यम";
  }
  
  // Adjust for category
  if (category.includes('SERVICE')) {
    area = Math.min(area, 300);
    power = "सिंगल फेज";
    risk = "कम";
  } else if (category.includes('AGRO')) {
    risk = "कम";
  } else if (category.includes('CHEMICAL') || category.includes('POLYMER')) {
    risk = "मध्यम";
    power = "थ्री फेज";
  }
  
  return {
    monthly_profit,
    monthly_profit_hindi: `₹${monthly_profit.toLocaleString('hi-IN')}/माह`,
    area_required: area,
    area_required_hindi: `${area} वर्ग फीट`,
    power_type_hindi: power,
    risk_level_hindi: risk
  };
};

export const ProjectCard = ({ project, index }) => {
  const [highlights, setHighlights] = useState(null);
  
  useEffect(() => {
    // Use estimated highlights immediately for fast rendering
    const estimated = getEstimatedHighlights(project);
    setHighlights(estimated);
    
    // If project has cached highlights, use those
    if (project.highlights) {
      setHighlights(project.highlights);
    }
  }, [project]);

  const handleQuickShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const message = `🏭 *${project.name_hindi}*
(${project.name})

📊 श्रेणी: ${project.category}
💰 अनुमानित लागत: ${formatCostFull(project.total_cost)}
${highlights ? `💹 अनुमानित मुनाफा: ${highlights.monthly_profit_hindi}` : ''}

📝 ${project.description_hindi || project.description || 'विस्तृत प्रोजेक्ट रिपोर्ट उपलब्ध'}

🔗 विस्तृत जानकारी के लिए उद्यमी मित्र देखें!

---
*उद्यमी मित्र* - अपना उद्योग शुरू करें! 🚀`;
    
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
    toast.success("WhatsApp पर शेयर करें");
  };

  return (
    <Link
      to={`/project/${project.id}`}
      className="group block p-5 bg-white rounded-xl border border-gray-200 hover:border-orange-300 hover:shadow-lg hover:shadow-orange-100 transition-all duration-300"
      style={{ animationDelay: `${index * 50}ms` }}
      data-testid={`project-card-${project.id}`}
    >
      {/* Header Section */}
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <Badge className={`${getCategoryStyle(project.category)} border text-xs mb-2`}>
            {project.category}
          </Badge>
          <h3 className="font-rajdhani font-bold text-lg text-gray-800 group-hover:text-orange-500 transition-colors truncate">
            {project.name_hindi}
          </h3>
          <p className="text-sm text-gray-500 truncate">{project.name}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="flex items-center gap-1 text-orange-500 font-rajdhani font-bold text-xl font-tabular-nums">
            <IndianRupee className="w-4 h-4" />
            {formatCost(project.total_cost)}
          </div>
          <p className="text-xs text-gray-500">अनुमानित लागत</p>
        </div>
      </div>
      
      {/* Key Metrics Grid - The "Trailer" */}
      {highlights && (
        <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-gray-100">
          {/* Monthly Profit */}
          <div className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2">
            <TrendingUp className="w-4 h-4 text-green-600 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-gray-500">मुनाफा</p>
              <p className="text-sm font-semibold text-green-600 truncate">{highlights.monthly_profit_hindi}</p>
            </div>
          </div>
          
          {/* Area Required */}
          <div className="flex items-center gap-2 bg-blue-50 rounded-lg px-3 py-2">
            <MapPin className="w-4 h-4 text-blue-600 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-gray-500">जगह</p>
              <p className="text-sm font-semibold text-blue-600 truncate">{highlights.area_required_hindi}</p>
            </div>
          </div>
          
          {/* Power Type */}
          <div className="flex items-center gap-2 bg-yellow-50 rounded-lg px-3 py-2">
            <Zap className="w-4 h-4 text-yellow-600 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-gray-500">बिजली</p>
              <p className="text-sm font-semibold text-yellow-600 truncate">{highlights.power_type_hindi}</p>
            </div>
          </div>
          
          {/* Risk Level */}
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
            <AlertTriangle className={`w-4 h-4 ${getRiskColor(highlights.risk_level_hindi)} shrink-0`} />
            <div className="min-w-0">
              <p className="text-xs text-gray-500">जोखिम</p>
              <p className={`text-sm font-semibold truncate ${getRiskColor(highlights.risk_level_hindi)}`}>
                {highlights.risk_level_hindi}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Footer */}
      <div className="mt-4 flex items-center justify-between">
        <button
          onClick={handleQuickShare}
          className="flex items-center gap-1 text-xs text-green-600 hover:text-green-500 transition-colors opacity-0 group-hover:opacity-100"
          data-testid={`quick-share-${project.id}`}
        >
          <MessageCircle className="w-3 h-3" />
          शेयर
        </button>
        <span className="flex items-center gap-1 text-sm text-orange-500 opacity-0 group-hover:opacity-100 transition-opacity">
          विवरण देखें <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </Link>
  );
};
